import type { BaseEntity } from './common';

/** Ad/promotion type. */
export type PromotionType = 'DISCOUNT' | 'CASHBACK' | 'LOYALTY' | 'FREEBIE';

export type PromotionStatus = 'DRAFT' | 'PENDING_REVIEW' | 'ACTIVE' | 'PAUSED' | 'EXPIRED' | 'REJECTED';

/** Ad/promotion published by a merchant. */
export interface PromotionDto extends BaseEntity {
  merchantId: string;
  merchantName?: string;
  title: string;
  description: string;
  type: PromotionType;
  discountPercent?: number;
  cashbackPercent?: number;
  imageUrl?: string;
  status: PromotionStatus;
  highlighted: boolean;
  startsAt: string;
  endsAt: string;
  views: number;
  redemptions: number;
  maxRedemptions?: number;
  rejectionReason?: string;
  /**
   * Condições que o anúncio mostra ao consumidor, na ordem em que o lojista as
   * arrumou. O core devolve sempre uma lista - vazia quando o anúncio não tem
   * condição nenhuma -, mas anúncios lidos de um backend antigo chegam sem o
   * campo, então o editor trata `undefined` como lista vazia.
   */
  rules?: PromotionRuleDto[];
  /**
   * O benefício configurado do anúncio (card #188). Ausente em anúncio anterior
   * ao motor cujo lojista ainda não editou - a tela mostra `discountPercent` /
   * `cashbackPercent` nesse caso, como sempre mostrou.
   */
  benefit?: PromotionBenefitDto | null;
  /**
   * As fotos da galeria do anúncio, na ordem em que o lojista as arrumou
   * (card #207, spec 034).
   *
   * O core devolve sempre uma lista - `[]` quando o anúncio não tem foto
   * nenhuma, que é o estado de todo anúncio anterior a este card. É opcional
   * aqui só porque um anúncio lido de um backend antigo chega sem o campo; o
   * editor trata `undefined` como lista vazia.
   */
  galleryUrls?: string[];
}

/**
 * Uma condição do anúncio como o EDITOR a lê de volta.
 *
 * Vem com `templateId`/`customText` além do texto pronto porque o formulário
 * precisa remarcar a cláusula certa do catálogo. Se só tivesse o texto, o
 * editor até desenharia a lista, mas ao salvar devolveria tudo como texto
 * próprio: cada anúncio ficaria com sua cópia da frase, e corrigir o texto no
 * catálogo deixaria de corrigir os anúncios que apontam para ele.
 */
export interface PromotionRuleDto {
  /** Preenchido quando é cláusula do catálogo; ausente quando é texto da loja. */
  templateId?: string;
  /** Preenchido quando é texto próprio da loja; ausente quando vem do catálogo. */
  customText?: string;
  /** O que o consumidor lê: o texto do catálogo ou o texto próprio. */
  text: string;
}

/**
 * Uma condição como o editor a ENVIA: ou a cláusula do catálogo, ou um texto
 * próprio - nunca os dois. É o `PromotionRuleInput` do core.
 */
export interface PromotionRuleInputDto {
  templateId?: string;
  customText?: string;
}

/** Uma cláusula do catálogo, como o formulário de anúncio do lojista a recebe. */
export interface PromotionRuleTemplateOptionDto {
  id: string;
  text: string;
}

export interface PromotionRequestDto {
  title: string;
  description: string;
  type: PromotionType;
  discountPercent?: number;
  cashbackPercent?: number;
  imageUrl?: string;
  highlighted?: boolean;
  startsAt: string;
  endsAt: string;
  maxRedemptions?: number;
  /**
   * Condições do anúncio.
   *
   * `undefined` significa "o formulário não desenhou este campo": na edição o
   * conjunto atual fica como está e, na criação, o anúncio nasce com as
   * cláusulas marcadas como padrão no catálogo. Uma lista - vazia inclusive -
   * substitui o conjunto. Mesma convenção de `businessHours`/`externalLinks`.
   */
  rules?: PromotionRuleInputDto[];
  /**
   * `undefined` significa "o formulário não desenhou este campo": o benefício
   * atual fica como está. Mesma convenção de `rules`.
   */
  benefit?: PromotionBenefitRequestDto;
  /**
   * As fotos da galeria do anúncio (card #207, spec 034 §4).
   *
   * **Este campo NÃO segue a convenção de `rules`/`benefit` acima, e por isso
   * é obrigatório aqui.** No core ele é `List<String>` não anulável com
   * `= emptyList()`: um `PUT` que omita `galleryUrls` não significa "não mexi",
   * significa **lista vazia - e apaga a galeria do anúncio**. A lista sempre
   * substitui o conjunto inteiro, na ordem enviada.
   *
   * O tipo é obrigatório de propósito: é o compilador quem impede o `PUT`
   * parcial que apagaria as fotos em silêncio. Quem salvar só o título tem de
   * reenviar as fotos que já existem - não há como esquecer sem quebrar o
   * build. Mesma escolha já feita em `MerchantProfileUpdateRequestDto`.
   *
   * As URLs são as que `POST /v1/merchant/profile/images` com
   * `target: "GALLERY"` devolveu: o mesmo upload da imagem de capa, nenhum
   * caminho novo.
   */
  galleryUrls: string[];
}

/** Moderation decision (backoffice). */
export interface PromotionModerationDto {
  approved: boolean;
  rejectionReason?: string;
}

/** Aggregated counters for the merchant "Anúncios" summary cards (global, não só da página). */
export interface PromotionSummaryDto {
  total: number;
  active: number;
  pendingReview: number;
  paused: number;
  rejected: number;
  expired: number;
  draft: number;
  highlighted: number;
  totalViews: number;
  totalRedemptions: number;
}

/**
 * Uma cláusula do catálogo de regras, como a OPERAÇÃO a mantém no backoffice.
 *
 * Diferente da que o painel do lojista recebe (`PromotionRuleTemplateOptionDto`,
 * só `id` e `text`, só as ativas): aqui vêm também os controles, porque esta é
 * a tela que os define.
 */
export interface PromotionRuleTemplateDto {
  id: string;
  text: string;
  /** Vem marcada quando o lojista abre um anúncio novo. */
  appliesByDefault: boolean;
  /** Cláusula inativa sai do formulário e continua legível nos anúncios antigos. */
  active: boolean;
  sortOrder: number;
  createdAt: string;
  /**
   * Nulo enquanto a cláusula nunca foi editada - o core devolve `updatedAt`
   * anulável e não vale herdar `BaseEntity`, que o declara obrigatório.
   */
  updatedAt?: string | null;
}

export interface PromotionRuleTemplateRequestDto {
  text: string;
  appliesByDefault: boolean;
  active: boolean;
  sortOrder: number;
}

// ==========================================================================
// Motor de tipos de benefício (card #188)
// ==========================================================================

/**
 * A forma de calcular o benefício - o lado CÓDIGO do motor, no core.
 *
 * O painel usa isto só para decidir **qual pré-visualização mostrar** (simular
 * uma conta faz sentido em desconto, não em brinde). A conta em si é sempre do
 * backend: uma segunda implementação aqui faria o lojista configurar olhando um
 * número que o balcão não vai repetir.
 */
export type BenefitValueShape = 'PERCENT' | 'FIXED_AMOUNT' | 'FREE_UNITS' | 'CASHBACK_PERCENT' | 'NONE';

/** Quem pode resgatar o benefício. */
export type BenefitAudience = 'EVERYONE' | 'FIRST_VISIT' | 'RETURNING' | 'BIRTHDAY';

/** Dia da semana como o core o serializa (`java.time.DayOfWeek`). */
export type BenefitWeekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/**
 * Um tipo de benefício do catálogo, como o formulário o recebe.
 *
 * Os `requires*` vêm do backend de propósito: é o que permite abrir um tipo novo
 * no backoffice sem deploy do painel. Se a tela embutisse a lista de campos por
 * tipo, o catálogo administrável não serviria para nada.
 */
export interface BenefitTypeDto {
  code: string;
  label: string;
  description?: string | null;
  valueShape: BenefitValueShape;
  requiresPercent: boolean;
  requiresAmount: boolean;
  requiresUnits: boolean;
  requiresItemLabel: boolean;
  icon?: string | null;
  /** Público a pré-selecionar ao escolher este tipo. */
  defaultAudience?: BenefitAudience | null;
  /** Se o painel deve pré-marcar "relâmpago". */
  defaultFlash: boolean;
}

/** O benefício de um anúncio, como o editor o lê de volta. */
export interface PromotionBenefitDto {
  typeCode: string;
  typeLabel: string;
  valueShape: BenefitValueShape;
  percentValue?: number | null;
  amountValue?: number | null;
  paidUnits?: number | null;
  freeUnits?: number | null;
  itemLabel?: string | null;
  maxDiscountAmount?: number | null;
  minPurchaseAmount?: number | null;
  /** Vazio = todos os dias. */
  weekdays: BenefitWeekday[];
  /** `"18:00"`, ou ausente quando vale o dia inteiro. */
  startsTime?: string | null;
  endsTime?: string | null;
  audience: BenefitAudience;
  returningMinRedemptions?: number | null;
  requiredPassPlanId?: string | null;
  requiredPassPlanName?: string | null;
  perCustomerLimit?: number | null;
  flash: boolean;
  /** A frase pronta que o cliente lê. Vem do core, nunca é montada aqui. */
  customerLabel: string;
  /** "Segunda a quinta, das 18h às 23h", ou ausente. */
  windowLabel?: string | null;
}

/** O benefício como o editor o envia. */
export interface PromotionBenefitRequestDto {
  typeCode: string;
  percentValue?: number;
  amountValue?: number;
  paidUnits?: number;
  freeUnits?: number;
  itemLabel?: string;
  maxDiscountAmount?: number;
  minPurchaseAmount?: number;
  /** Ausente ou vazio = todos os dias. */
  weekdays?: BenefitWeekday[];
  startsTime?: string;
  endsTime?: string;
  audience?: BenefitAudience;
  returningMinRedemptions?: number;
  /** Ausente = qualquer plano, inclusive o gratuito. */
  requiredPassPlanId?: string;
  perCustomerLimit?: number;
  flash?: boolean;
}

export interface BenefitPreviewRequestDto {
  benefit: PromotionBenefitRequestDto;
  /** Conta de exemplo. Ausente = só o rótulo. */
  purchaseAmount?: number;
}

/** O que a pré-visualização devolve - tudo calculado no core. */
export interface BenefitPreviewDto {
  customerLabel: string;
  windowLabel?: string | null;
  audienceLabel?: string | null;
  /** Ausente quando a forma não abate conta, ou quando não há conta de exemplo. */
  discountAmount?: number | null;
  cashbackAmount?: number | null;
  finalAmount?: number | null;
  belowMinimumPurchase: boolean;
}

/** Rótulos pt-BR dos públicos, para o seletor do formulário. */
export const BENEFIT_AUDIENCE_LABELS: Record<BenefitAudience, string> = {
  EVERYONE: 'Qualquer cliente',
  FIRST_VISIT: 'Primeira visita à loja',
  RETURNING: 'Cliente recorrente',
  BIRTHDAY: 'Aniversariante',
};

/** Dias da semana na ordem segunda → domingo, com o rótulo curto do painel. */
export const BENEFIT_WEEKDAYS: ReadonlyArray<{ value: BenefitWeekday; label: string }> = [
  { value: 'MONDAY', label: 'Seg' },
  { value: 'TUESDAY', label: 'Ter' },
  { value: 'WEDNESDAY', label: 'Qua' },
  { value: 'THURSDAY', label: 'Qui' },
  { value: 'FRIDAY', label: 'Sex' },
  { value: 'SATURDAY', label: 'Sáb' },
  { value: 'SUNDAY', label: 'Dom' },
];

// ==========================================================================
// Catálogo de tipos de benefício, como a OPERAÇÃO o mantém (card #361)
// ==========================================================================

/**
 * Um tipo de benefício visto pelo backoffice.
 *
 * Diferente do `BenefitTypeDto` que o painel do lojista recebe (só os ativos, e
 * só o que o formulário dele precisa): aqui vêm também os controles - `active`,
 * `sortOrder`, `labelTemplate` - porque esta é a tela que os define.
 */
export interface BenefitTypeAdminDto {
  id: string;
  /** Identificador estável. Escolhido na criação e nunca editado depois. */
  code: string;
  label: string;
  description?: string | null;
  /** A forma de calcular. Escolhida na criação; é a fronteira dado/código. */
  valueShape: BenefitValueShape;
  requiresPercent: boolean;
  requiresAmount: boolean;
  requiresUnits: boolean;
  /** A única exigência de livre escolha: se a loja precisa dizer "o quê". */
  requiresItemLabel: boolean;
  /** Frase que o cliente lê, com marcadores resolvidos no core. */
  labelTemplate: string;
  /** Nome do ícone no lucide, como o V84 os gravou (`package-plus`). */
  icon?: string | null;
  defaultAudience?: BenefitAudience | null;
  defaultFlash: boolean;
  /** Inativo sai do formulário do lojista e continua legível nos anúncios. */
  active: boolean;
  sortOrder: number;
  /**
   * Quantos anúncios apontam para este tipo.
   *
   * Ausente quando o backend não o envia: a tela então trata como
   * "desconhecido" e continua impedindo a troca da forma - que é imutável de
   * qualquer maneira -, apenas sem dizer o número.
   */
  usageCount?: number;
  createdAt?: string;
  updatedAt?: string | null;
}

/**
 * Criação e edição.
 *
 * `code` e `valueShape` só valem na criação: o primeiro é o identificador que
 * core e frontends usam para falar do tipo, e o segundo é a aritmética. Trocar
 * a forma reprecificaria em silêncio os anúncios no ar - e o banco recusa, pela
 * chave estrangeira composta `fk_promotion_benefit_type_shape`.
 *
 * Os `requires*` de valor não estão aqui: eles são **derivados** da forma
 * (`ck_benefit_type_requirements`), e mandá-los soltos só criaria a chance de
 * um tipo pedir percentual ao lojista e ser calculado como valor fixo.
 */
export interface BenefitTypeRequestDto {
  /** Só na criação; ignorado na edição. */
  code?: string;
  /** Só na criação; ignorado na edição. */
  valueShape?: BenefitValueShape;
  label: string;
  description?: string;
  labelTemplate: string;
  requiresItemLabel: boolean;
  icon?: string;
  defaultAudience?: BenefitAudience;
  defaultFlash: boolean;
  active: boolean;
  sortOrder: number;
}

/** Como cada forma se chama na tela. */
export const BENEFIT_VALUE_SHAPE_LABELS: Record<BenefitValueShape, string> = {
  PERCENT: 'Percentual sobre a compra',
  FIXED_AMOUNT: 'Valor fixo abatido',
  FREE_UNITS: 'Unidades grátis (compre X, leve Y)',
  CASHBACK_PERCENT: 'Percentual de volta na carteira',
  NONE: 'Sem valor calculável (brinde, upgrade, cortesia)',
};

/** O que cada forma faz com a conta - o texto que a tela mostra ao escolher. */
export const BENEFIT_VALUE_SHAPE_HELP: Record<BenefitValueShape, string> = {
  PERCENT: 'Abate um percentual do valor da compra, com teto opcional em reais.',
  FIXED_AMOUNT: 'Abate um valor fixo, nunca maior que a própria conta.',
  FREE_UNITS: 'Não vira dinheiro: o balcão entrega unidades a mais.',
  CASHBACK_PERCENT:
    'Descreve quanto volta na carteira. Quem credita continua sendo o resgate do cupom - o motor não credita duas vezes.',
  NONE: 'O benefício existe e é entregue no balcão, mas não abate a conta. Atribuir um número a ele seria inventar preço para cortesia.',
};

/**
 * Que campo de valor cada forma exige do lojista.
 *
 * Espelha `ck_benefit_type_requirements` (V84): é o banco que amarra as
 * exigências à forma, e a tela apenas mostra o que já está decidido, em vez de
 * oferecer combinações que o `INSERT` vai recusar.
 */
export const BENEFIT_VALUE_SHAPE_REQUIREMENTS: Record<
  BenefitValueShape,
  { percent: boolean; amount: boolean; units: boolean }
> = {
  PERCENT: { percent: true, amount: false, units: false },
  CASHBACK_PERCENT: { percent: true, amount: false, units: false },
  FIXED_AMOUNT: { percent: false, amount: true, units: false },
  FREE_UNITS: { percent: false, amount: false, units: true },
  NONE: { percent: false, amount: false, units: false },
};

/**
 * Marcadores que o core resolve em `BenefitAmounts.renderLabel`.
 *
 * `{item}` só faz sentido quando o tipo exige o nome do item; os demais só
 * quando a forma preenche o valor correspondente. Um marcador que a forma não
 * preenche é substituído por vazio - a frase não quebra, mas fica capenga.
 */
export const BENEFIT_LABEL_PLACEHOLDERS: Record<BenefitValueShape, string[]> = {
  PERCENT: ['{percent}'],
  CASHBACK_PERCENT: ['{percent}'],
  FIXED_AMOUNT: ['{amount}'],
  FREE_UNITS: ['{paid}', '{free}'],
  NONE: [],
};

/** Copy para os `errorCode` do catálogo de tipos. */
export const BENEFIT_TYPE_ERROR_MESSAGES: Record<string, string> = {
  BENEFIT_TYPE_CODE_EXISTS:
    'Já existe um tipo com esse código. Procure por ele na lista - se estiver inativo, reative em vez de criar outro.',
  BENEFIT_TYPE_IN_USE:
    'Este tipo está em uso por anúncios publicados e não pode ser removido. Desative-o: ele sai do formulário do lojista e continua legível no que já existe.',
  BENEFIT_TYPE_SHAPE_IMMUTABLE:
    'A forma de cálculo não muda depois da criação - trocá-la reprecificaria em silêncio os anúncios no ar. Crie um tipo novo com a forma certa e desative este.',
};

/**
 * Teto de fotos por anúncio. Espelha `PromotionGalleryService.MAX_IMAGES` no
 * core - dez porque é anúncio, não álbum (spec 034 §3).
 *
 * A tela usa esta constante para não deixar o lojista chegar na 11ª foto. Isso
 * não dispensa tratar o 400 do servidor: a validação de tela é conveniência, a
 * do core é a regra.
 */
export const PROMOTION_GALLERY_MAX_COUNT = 10;

/**
 * Copy para os `errorCode` que o core devolve ao recusar a galeria do anúncio.
 *
 * **A chave foi conferida no core**, em
 * `PromotionGalleryService.LIMIT_ERROR_CODE`, e não deduzida pela convenção de
 * nenhum outro mapa. É a mesma lição do #218: chave errada aqui não quebra
 * nada visivelmente - o `extractErrorMessage` só cai na mensagem crua do
 * servidor ("O anúncio pode ter no máximo 10 fotos na galeria") e a frase útil
 * nunca chega ao lojista. Defeito silencioso.
 */
export const PROMOTION_GALLERY_ERROR_MESSAGES: Record<string, string> = {
  PROMOTION_GALLERY_LIMIT_REACHED: `Um anúncio comporta ${PROMOTION_GALLERY_MAX_COUNT} fotos. Remova uma para incluir outra.`,
};
